<template>
  <div class="verify-page">
    <div class="verify-container">
      <div class="verify-header">
        <img src="@/assets/image/header-logo.png" alt="eClub Logo" class="verify-logo" />
        <h1 class="verify-title">Certificate Verification</h1>
        <p class="verify-subtitle">Verify the authenticity of eClub certificates</p>
      </div>

      <div class="verify-form">
        <el-form :model="form" :rules="rules" ref="verifyForm" label-position="top">
          <el-form-item label="Certificate ID" prop="certificateId">
            <el-input
              v-model="form.certificateId"
              placeholder="Enter your certificate ID"
              @keyup.enter.native="verifyCertificate">
            </el-input>
          </el-form-item>

          <el-form-item label="Certificate Type (optional)">
            <el-select v-model="form.type" placeholder="Auto-detect if not selected" style="width: 100%" clearable>
              <el-option label="Membership Certificate" value="membership"></el-option>
              <el-option label="Leadership Certificate" value="leadership"></el-option>
              <el-option label="Advisory Certificate" value="advisory"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item>
            <el-button
              type="primary"
              @click="verifyCertificate"
              :loading="loading"
              style="width: 100%">
              Verify Certificate
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- Verification Result -->
      <div v-if="verificationResult" class="verify-result" :class="verificationResult.valid ? 'valid' : 'invalid'">
        <div class="result-icon">
          <i :class="verificationResult.valid ? 'el-icon-success' : 'el-icon-error'"></i>
        </div>
        <div class="result-content">
          <h3 class="result-title">{{ verificationResult.valid ? 'Certificate Verified' : 'Certificate Not Found' }}</h3>
          <p class="result-message">{{ verificationResult.message }}</p>

          <div v-if="verificationResult.valid && verificationResult.data" class="result-details">
            <div class="detail-row">
              <span class="detail-label">Certificate Type:</span>
              <span class="detail-value">{{ formatCertificateType(verificationResult.data.type) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Recipient Name:</span>
              <span class="detail-value">{{ verificationResult.data.userName }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Club:</span>
              <span class="detail-value">{{ verificationResult.data.clubName }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">School:</span>
              <span class="detail-value">{{ verificationResult.data.schoolName }}</span>
            </div>
            <div class="detail-row" v-if="verificationResult.data.position">
              <span class="detail-label">Position:</span>
              <span class="detail-value">{{ verificationResult.data.position }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Issue Date:</span>
              <span class="detail-value">{{ verificationResult.data.issuedDate }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="verify-footer">
        <p>This verification system confirms certificates issued by the eClub Global System.</p>
        <p>For questions, please contact support@eclub.center</p>
      </div>
    </div>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'

export default {
  name: 'CertificateVerify',
  data() {
    return {
      form: {
        type: '',
        certificateId: ''
      },
      rules: {
        certificateId: [
          { required: true, message: 'Please enter certificate ID', trigger: 'blur' },
          { min: 5, message: 'Certificate ID must be at least 5 characters', trigger: 'blur' }
        ]
      },
      loading: false,
      verificationResult: null
    }
  },

  created() {
    // Check if certificate ID is passed in URL
    const certId = this.$route.params.certificateId || this.$route.query.id
    if (certId) {
      this.form.certificateId = certId
      // Auto-verify when ID is provided via URL
      this.$nextTick(() => {
        this.verifyCertificate()
      })
    }
  },

  methods: {
    verifyCertificate() {
      this.$refs.verifyForm.validate(async (valid) => {
        if (!valid) return

        this.loading = true
        this.verificationResult = null

        try {
          const response = await postRequest('verifyCertificate', {
            certificate_id: this.form.certificateId,
            certificate_type: this.form.type
          })

          if (response.data && response.data.code === 0 && response.data.data) {
            this.verificationResult = {
              valid: true,
              message: 'This certificate is authentic and was issued by the eClub Global System.',
              data: response.data.data
            }
          } else {
            this.verificationResult = {
              valid: false,
              message: response.data?.msg || 'No certificate found with the provided ID and type. Please check the information and try again.'
            }
          }
        } catch (error) {
          console.error('Verification error:', error)
          this.verificationResult = {
            valid: false,
            message: 'Unable to verify certificate at this time. Please try again later.'
          }
        } finally {
          this.loading = false
        }
      })
    },

    formatCertificateType(type) {
      const types = {
        membership: 'Membership Certificate',
        leadership: 'Leadership Certificate',
        advisory: 'Advisory Certificate'
      }
      return types[type] || type
    }
  }
}
</script>

<style lang="scss" scoped>
.verify-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.verify-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  overflow: hidden;
}

.verify-header {
  background: linear-gradient(135deg, #FF6450 0%, #FF8A65 100%);
  padding: 40px 30px;
  text-align: center;
  color: white;

  .verify-logo {
    height: 50px;
    margin-bottom: 20px;
  }

  .verify-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  .verify-subtitle {
    font-size: 14px;
    opacity: 0.9;
    margin: 0;
  }
}

.verify-form {
  padding: 30px;

  :deep(.el-form-item__label) {
    font-weight: 500;
    color: #333;
  }

  :deep(.el-input__inner),
  :deep(.el-select .el-input__inner) {
    height: 44px;
    line-height: 44px;
  }

  :deep(.el-button--primary) {
    height: 44px;
    font-size: 16px;
    background-color: #FF6450;
    border-color: #FF6450;

    &:hover {
      background-color: #ff7d6b;
      border-color: #ff7d6b;
    }
  }
}

.verify-result {
  margin: 0 30px 30px;
  padding: 20px;
  border-radius: 12px;
  display: flex;
  gap: 16px;

  &.valid {
    background: #f0fdf4;
    border: 1px solid #86efac;

    .result-icon {
      color: #22c55e;
    }

    .result-title {
      color: #166534;
    }
  }

  &.invalid {
    background: #fef2f2;
    border: 1px solid #fca5a5;

    .result-icon {
      color: #ef4444;
    }

    .result-title {
      color: #991b1b;
    }
  }

  .result-icon {
    font-size: 32px;
    flex-shrink: 0;
  }

  .result-content {
    flex: 1;

    .result-title {
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 8px 0;
    }

    .result-message {
      font-size: 14px;
      color: #666;
      margin: 0 0 16px 0;
    }
  }

  .result-details {
    background: white;
    border-radius: 8px;
    padding: 16px;

    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .detail-label {
        color: #666;
        font-size: 13px;
      }

      .detail-value {
        color: #333;
        font-weight: 500;
        font-size: 13px;
        text-align: right;
      }
    }
  }
}

.verify-footer {
  padding: 20px 30px;
  background: #f9fafb;
  text-align: center;
  border-top: 1px solid #e5e7eb;

  p {
    margin: 0;
    font-size: 12px;
    color: #9ca3af;
    line-height: 1.6;
  }
}
</style>
